import Context from "shared/util/Context";
import Sookie from "shared/util/Sookie";
import OkC from "shared/util/OkcApi";
import { OPEN_MODELS } from "apps/staffbar/util/constants";


// ////////////////////////
// ACTION TYPES //////////
// //////////////////////

export const OPEN = "STAFFBAR.OPEN";
export const CLOSE = "STAFFBAR.CLOSE";
export const TOGGLE = "STAFFBAR.TOGGLE";
export const OPEN_DRAWER = "STAFFBAR.OPEN_DRAWER";
export const SET_OPEN_MODEL = "STAFFBAR.SET_OPEN_MODEL";
export const SET_PAGE_ERRORS = "STAFFBAR.SET_PAGE_ERRORS";
export const CHANGE_GOODIE = "STAFFBAR.CHANGE_GOODIE";
export const CHANGE_GOODIE_SUCCESS = "STAFFBAR.CHANGE_GOODIE_SUCCESS";
export const CHANGE_GOODIE_FAILURE = "STAFFBAR.CHANGE_GOODIE_FAILURE";
export const SET_EXPERIMENT_GROUP = "STAFFBAR.SET_EXPERIMENT_GROUP";
export const SET_EXPERIMENT_GROUP_SUCCESS = "STAFFBAR.SET_EXPERIMENT_GROUP_SUCCESS";
export const SET_EXPERIMENT_GROUP_FAILURE = "STAFFBAR.SET_EXPERIMENT_GROUP_FAILURE";
export const SET_CURRENT_GOODIE_SECTION = "STAFFBAR.SET_CURRENT_GOODIE_SECTION";


// ////////////////////////
// INITIAL STATE /////////
// //////////////////////

export const INITIAL_STATE = {
	// imported
	server: "",
	servers: [],
	page: "",
	services: "",
	loggedInInfo: {},
	profileInfo: {},
	profileLinks: [],
	goodies: {},
	staffLinks: {},
	blurbs: [],
	pageLinks: [],
	overrides: {},
	inspector: [],
	environments: [],
	experiments: [],
	appPages: [],
	errors: null,

	// app state
	currentGoodieSection: null,
	drawer: null,
	isExpanded: false,
	isStaff: false,
	isStaffUserName: false,
	alist: false,
	openModel: OPEN_MODELS.mouseover,
};

function getInitialState() {
	const state = typeof window !== "undefined" && window.StaffbarParams ?
		{ ...INITIAL_STATE, ...window.StaffbarParams } :
		INITIAL_STATE;

	const isMobile = process.env.PLATFORM !== "desktop";
	const isPinned = state.openModel === OPEN_MODELS.pinned;

	if (!isMobile && isPinned) {
		state.isExpanded = true;
	}

	return state;
}


// ////////////////////////
// REDUCER ///////////////
// //////////////////////

export function reducer(state = getInitialState(), action) {
	switch (action.type) {
		case OPEN: {
			return {
				...state,
				isExpanded: true,
			};
		}

		case CLOSE: {
			return {
				...state,
				isExpanded: false,
			};
		}

		case TOGGLE: {
			return {
				...state,
				isExpanded: !state.isExpanded,
			};
		}

		case OPEN_DRAWER: {
			return {
				...state,

				// re-selecting the current drawer closes it
				drawer: action.drawer !== state.drawer ?
					action.drawer :
					null,
			};
		}

		case SET_OPEN_MODEL: {
			return {
				...state,
				openModel: action.openModel,
				isExpanded: action.openModel === OPEN_MODELS.pinned || state.isExpanded,
			};
		}

		case SET_PAGE_ERRORS: {
			return {
				...state,
				errors: action.errors,
			};
		}

		case SET_CURRENT_GOODIE_SECTION: {
			return {
				...state,
				currentGoodieSection: action.section === state.currentGoodieSection ?
					null :
					action.section,
			};
		}

		case CHANGE_GOODIE: {
			const { key, section, isActivated } = action;

			return {
				...state,
				goodies: {
					...state.goodies,
					[section]: state.goodies[section].map((goodie) => {
						if (goodie.key === key) {
							return {
								...goodie,
								key,
								section,
								activated: isActivated,
								updating: true,
							};
						}

						return goodie;
					}),
				},
			};
		}

		case CHANGE_GOODIE_SUCCESS: {
			const { key, section, isActivated } = action;

			return {
				...state,
				goodies: {
					...state.goodies,
					[section]: state.goodies[section].map((goodie) => {
						if (goodie.key === key) {
							return {
								...goodie,
								key,
								section,
								activated: isActivated,
								updating: false,
							};
						}

						return goodie;
					}),
				},
			};
		}

		case CHANGE_GOODIE_FAILURE: {
			const { key, section, isActivated } = action;

			return {
				...state,
				goodies: {
					...state.goodies,
					[section]: state.goodies[section].map((goodie) => {
						if (goodie.key === key) {
							return {
								...goodie,
								key,
								section,
								activated: isActivated,
								updating: false,
							};
						}

						return goodie;
					}),
				},
			};
		}

		case SET_EXPERIMENT_GROUP: {
			const { experimentid, groupid } = action;

			const updatedExperiments = state.experiments.map((experiment) => {
				if (experiment.experimentid !== experimentid) {
					return experiment;
				}

				const updatedGroups = experiment.groups.map((group) => {
					return {
						...group,
						isActive: group.groupid === groupid,
					};
				});

				return {
					...experiment,
					groups: updatedGroups,
				};
			});

			return {
				...state,
				experiments: updatedExperiments,
				isUpdatingExperimentGroup: true,
			};
		}

		case SET_EXPERIMENT_GROUP_SUCCESS: {
			return {
				...state,
				isUpdatingExperimentGroup: false,
			};
		}

		case SET_EXPERIMENT_GROUP_FAILURE: {
			return {
				...state,
				isUpdatingExperimentGroup: false,
			};
		}

		default:
			return state;
	}
}


// ////////////////////////
// ACTION CREATORS ///////
// //////////////////////

export function open() {
	return { type: OPEN };
}

export function close() {
	return { type: CLOSE };
}

export function toggle() {
	return { type: TOGGLE };
}

export function openDrawer(drawer) {
	return { type: OPEN_DRAWER, drawer };
}

export function setOpenModel(openModel, { persist = true } = {}) {
	if (persist) {
		Sookie.set(
			"staffbar_open_model",
			openModel,
			86400 * 365,
			"196055b3f94d4bc5b5c160ac9908d020658f8d9c",
		);
	}

	if (openModel === OPEN_MODELS.pinned) {
		document.body.classList.add("staffbar-pinned");
	} else {
		document.body.classList.remove("staffbar-pinned");
	}

	return { type: SET_OPEN_MODEL, openModel };
}

export function setPageErrors(errors) {
	return { type: SET_PAGE_ERRORS, errors };
}

export function setExperimentGroup(experimentid, groupid) {
	return (dispatch) => {
		dispatch({
			type: SET_EXPERIMENT_GROUP,
			experimentid,
			groupid,
		});

		const data = {
			experimentid,
			groupid,
			userid: Context.get("userid"),
		};

		OkC.post("/experiments/set_user_group", { data }).then(() => {
			dispatch({
				type: SET_EXPERIMENT_GROUP_SUCCESS,
			});
		}).catch(() => {
			dispatch({
				type: SET_EXPERIMENT_GROUP_FAILURE,
			});
		});
	};
}

export function setCurrentGoodieSection(section) {
	return { type: SET_CURRENT_GOODIE_SECTION, section };
}

export function changeGoodie({ key, isActivated, section }) {
	return (dispatch) => {
		dispatch({
			type: CHANGE_GOODIE, key, isActivated, section,
		});

		// update the goodie
		OkC.post("/okc/staffgoodies", {
			data: { name: key, state: isActivated },
		}).then(() => {
			dispatch({
				type: CHANGE_GOODIE_SUCCESS, key, isActivated, section,
			});
		}).catch(() => {
			dispatch({
				type: CHANGE_GOODIE_FAILURE, key, isActivated: !isActivated, section,
			});
		});
	};
}



// WEBPACK FOOTER //
// ./src/ducks/staffbar.js